import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import styles from '../AgentVerificationTable.module.scss'

import { CountryCodeFlag } from '@percent/admin-dashboard/common/components/countryCodeFlag/CountryCodeFlag'
import { getDate } from '@percent/admin-dashboard/common/utility'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import { isEmail } from '@percent/admin-dashboard/common/utility/isEmail'
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { AvTableProps } from './shared/types'

const sortMapTableNamesToProperties: Record<string, string> = {
  requestDate: 'createdAt'
}

export function DefaultAvTable({
  isLoading,
  totalResults,
  validations,
  queryParams,
  setQueryParams,
  nextPage,
  previousPage,
  children
}: AvTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const columns: TableColumn[] = [
    { id: 'country', content: t('table.country'), isSortable: false, props: { width: '5%' } },
    { id: 'organization', content: t('table.organization'), isSortable: false, props: { width: '25%' } },
    { id: 'agentName', content: t('table.agentName'), isSortable: false, props: { width: '15%' } },
    { id: 'agentEmail', content: t('table.agentEmail'), isSortable: false, props: { width: '20%' } },
    { id: 'partner', content: t('table.partner'), isSortable: false, props: { width: '20%' } },
    { id: 'requestDate', content: t('table.requestDate'), isSortable: true, props: { width: '15%' } }
  ]

  const handleRequestSort = (columnKey: string) => {
    setQueryParams({
      direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC',
      sort: sortMapTableNamesToProperties[columnKey]
    })
  }

  return (
    <TableProvider
      disableHorizontalScroll
      sortByColumn={queryParams.sort ?? undefined}
      sortDirection={(queryParams.direction?.toUpperCase() as TableColumnSortDirection) ?? 'ASC'}
      handleRequestSort={handleRequestSort}
      columns={columns}
      emptyTableTitle={t('table.emptyAgentVerifications')}
      illustration="no-results"
      isLoading={isLoading}
      totalResults={totalResults}
    >
      {children}
      <Table>
        <TableBody>
          {validations?.map(agentVerification => (
            <TableRow
              key={agentVerification.id}
              data-testid="agent-verification-row"
              className={agentVerification.organisationId ? styles.row : undefined}
              onClick={handleClickAsLink(`/agent-verification/${agentVerification.id}`, navigate)}
            >
              <TableCell>
                <CountryCodeFlag
                  countryCode={
                    agentVerification.organisation?.countryCode ?? agentVerification.validationRequest?.countryCode
                  }
                />
              </TableCell>
              <TableCell>{agentVerification.organisation?.name || agentVerification.validationRequest?.name}</TableCell>
              <TableCell>{`${agentVerification.firstName} ${agentVerification.lastName}`}</TableCell>
              <TableCell>
                {isEmail(agentVerification.email)
                  ? agentVerification.email.substring(0, agentVerification.email.lastIndexOf('@'))
                  : agentVerification.email || t('status.notAvailable')}
              </TableCell>
              <TableCell>{agentVerification?.partner?.name ?? ''}</TableCell>
              <TableCell>{getDate(agentVerification.createdAt)}</TableCell>
              <TableCell className={styles.rowIconForward}>
                <Icon name="chevron-right" color="gray400" size={4} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        nextPage={nextPage}
        previousPage={previousPage}
        totalResultsText={t('table.results', { count: totalResults })}
      />
    </TableProvider>
  )
}
